// Project "Preview & tests" tab (F05): preview targets, test accounts (credentials write-only;
// only their state returns), browser sessions, declarative scenarios and browser runs. The
// browser executor is not installed (D11.1) and staging runs need human authorization (D11.2):
// run/capture requests answer a structured BLOCKED result, rendered exactly as refused.
// Every surface carries the server's "URL filter, not network isolation" label.
import { useState, type FormEvent } from "react";
import { useI18n } from "../../i18n/index.tsx";
import type { MessageKey } from "../../i18n/en.ts";
import {
  capturePreview,
  createPreviewTarget,
  createScenario,
  createTestAccount,
  disableTestAccount,
  getPreview,
  listBrowserRuns,
  listBrowserSessions,
  listScenarios,
  listTestAccounts,
  putTestAccountSecret,
  revokeBrowserSession,
  revokeTestAccountTotp,
  setScenarioState,
  startBrowserRun,
  updateTestAccount,
} from "../../api/endpoints-wave3.ts";
import { isApiError } from "../../api/client.ts";
import { URL_FILTER_LABEL, type BrowserRun, type BrowserScenario, type PreviewTarget, type TestAccount } from "../../api/types-wave3.ts";
import { useAsync, type AsyncState } from "../../lib/useAsync.ts";
import { Button } from "../../components/Button.tsx";
import { Dialog } from "../../components/Dialog.tsx";
import { SecretField, SelectField, TextAreaField, TextField } from "../../components/Field.tsx";
import { EmptyState, ErrorBanner, LoadingBlock } from "../../components/Feedback.tsx";
import { ActionError, fieldErrors } from "../../components/ActionError.tsx";
import { Pill } from "../../components/Status.tsx";
import { useToast } from "../../components/Toast.tsx";
import { KeyIcon, MonitorIcon, PlayCircleIcon, RefreshIcon, UserIcon } from "../../components/icons.tsx";
import { FormSection } from "../config/shared.tsx";
import { ConfirmDialog, Hash, ShortId, StatePill, splitList, useProjectResources, useProjectRole, type ProjectEnvironment } from "../wave3/common.tsx";
import { EgressLabel, PreviewFacts, PreviewSurface } from "./Preview.tsx";
import { UnpublishedPreview, type PublicationContext } from "../microsystem/Publication.tsx";

export function BrowserTab({ projectId, publication }: { projectId: string; publication?: PublicationContext }) {
  const { t } = useI18n();
  const resources = useProjectResources(projectId);
  const accounts = useAsync((s) => listTestAccounts(projectId, s), [projectId]);
  return (
    <div className="section-stack" data-testid="browser-tab">
      <p className="field-hint">
        {t("browser.intro")} <EgressLabel label={URL_FILTER_LABEL} />
      </p>
      <PreviewSection projectId={projectId} environments={resources.environments} publication={publication} />
      <TestAccountsSection projectId={projectId} environments={resources.environments} accounts={accounts} />
      <ScenariosSection projectId={projectId} environments={resources.environments} accounts={accounts.data?.items ?? []} />
    </div>
  );
}

function envName(envs: ProjectEnvironment[], id: string | null) {
  if (!id) return null;
  return envs.find((e) => e.id === id)?.name ?? null;
}

// ---------------- Preview ----------------

function PreviewSection({ projectId, environments, publication }: { projectId: string; environments: ProjectEnvironment[]; publication?: PublicationContext | undefined }) {
  const { t } = useI18n();
  const role = useProjectRole(projectId);
  // A succeeded publish registers the app as a preview target (D31): read the targets again when it lands.
  const published = publication?.publication.publish?.state === "succeeded";
  const preview = useAsync((s) => getPreview(projectId, s), [projectId, published]);
  const items = preview.data?.items ?? [];
  return (
    <section className="card" aria-labelledby="preview-targets-title" data-testid="preview-section">
      <div className="card-header">
        <MonitorIcon size={18} />
        <h2 id="preview-targets-title" className="card-title" style={{ flex: 1 }}>
          {t("preview.title")}
        </h2>
        <Button size="sm" variant="ghost" icon={<RefreshIcon size={14} />} onClick={preview.reload}>
          {t("common.refresh")}
        </Button>
      </div>
      <div className="card-body stack">
        {preview.loading ? (
          <LoadingBlock label={t("common.loading")} />
        ) : preview.error ? (
          <ErrorBanner error={preview.error} onRetry={preview.reload} title={t("preview.loadFailed")} />
        ) : items.length === 0 && publication ? (
          <UnpublishedPreview projectId={projectId} {...publication} />
        ) : items.length === 0 ? (
          <EmptyState icon={<MonitorIcon />} title={t("preview.emptyTitle")} headingLevel={3}>
            <p>{t("preview.emptyBody")}</p>
          </EmptyState>
        ) : (
          <ul className="config-list">
            {items.map((target) => (
              <li key={target.id}>
                <PreviewTargetCard target={target} canCapture={role.atLeast("operator")} environments={environments} onChanged={preview.reload} />
              </li>
            ))}
          </ul>
        )}
        {role.atLeast("maintainer") ? <PreviewTargetForm projectId={projectId} environments={environments} onCreated={preview.reload} /> : null}
        {items[0]?.note ? <p className="field-hint">{items[0].note}</p> : null}
      </div>
    </section>
  );
}

function PreviewTargetCard({ target, canCapture, environments, onChanged }: { target: PreviewTarget; canCapture: boolean; environments: ProjectEnvironment[]; onChanged: () => void }) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const capture = async () => {
    setBusy(true);
    setError(null);
    try {
      await capturePreview(target.id, target.version.current_candidate_hash);
      onChanged();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="card preview-panel" data-testid="preview-target" data-display={target.display_state}>
      <div className="preview-bar">
        <span className="preview-url" title={target.url}>
          {target.url}
        </span>
      </div>
      <PreviewFacts target={target} />
      <PreviewSurface
        target={target}
        extra={
          canCapture ? (
            <Button onClick={() => void capture()} loading={busy}>
              {t("preview.capture")}
            </Button>
          ) : null
        }
      />
      <div className="card-body stack" style={{ gap: 6 }}>
        <p className="subtle">
          {t("preview.environment")}: {envName(environments, target.environment_id) ?? (target.environment_id ? <ShortId id={target.environment_id} /> : t("preview.noEnvironment"))}
          {target.task_id ? (
            <>
              {" · "}
              {t("preview.task")}: <ShortId id={target.task_id} />
            </>
          ) : null}
        </p>
        <ActionError error={error} onDismiss={() => setError(null)} />
      </div>
    </div>
  );
}

function PreviewTargetForm({ projectId, environments, onCreated }: { projectId: string; environments: ProjectEnvironment[]; onCreated: () => void }) {
  const { t } = useI18n();
  const { toast } = useToast();
  const [url, setUrl] = useState("");
  const [env, setEnv] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [local, setLocal] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!/^https?:\/\/\S+$/.test(url.trim())) {
      setLocal(t("preview.urlFormat"));
      return;
    }
    setLocal(null);
    setBusy(true);
    setError(null);
    try {
      await createPreviewTarget(projectId, { url: url.trim(), environment_id: env || null, task_id: null });
      setUrl("");
      toast(t("preview.registered"), "success");
      onCreated();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  const fe = fieldErrors(error);
  return (
    <details className="disclosure">
      <summary>{t("preview.register")}</summary>
      <form className="stack" onSubmit={submit} noValidate data-testid="preview-form">
        <p className="field-hint">{t("preview.registerHint")}</p>
        <ActionError error={error} />
        <div className="w3-toolbar">
          <TextField label={t("preview.url")} type="url" value={url} onChange={(e) => setUrl(e.target.value)} error={local ?? fe["url"] ?? null} spellCheck={false} placeholder="https://" />
          <SelectField label={t("preview.environment")} value={env} onChange={(e) => setEnv(e.target.value)} error={fe["environment_id"] ?? null}>
            <option value="">{t("preview.noEnvironment")}</option>
            {environments.map((x) => (
              <option key={x.id} value={x.id}>
                {x.name} ({x.kind})
              </option>
            ))}
          </SelectField>
          <Button type="submit" variant="primary" loading={busy}>
            {t("preview.registerSubmit")}
          </Button>
        </div>
      </form>
    </details>
  );
}

// ---------------- Test accounts & sessions ----------------

function SecretPill({ state, label }: { state: "missing" | "active" | "revoked"; label: string }) {
  const { t } = useI18n();
  const tone = state === "active" ? "success" : state === "revoked" ? "danger" : "neutral";
  return (
    <span className="row" data-testid="secret-state" data-state={state}>
      <span className="subtle">{label}</span>
      <Pill tone={tone} icon={state === "active" ? "check" : state === "revoked" ? "ban" : "circle"}>
        {t(`secret.${state}`)}
      </Pill>
    </span>
  );
}

function TestAccountsSection({
  projectId,
  environments,
  accounts,
}: {
  projectId: string;
  environments: ProjectEnvironment[];
  accounts: AsyncState<{ items: TestAccount[] }>;
}) {
  const { t } = useI18n();
  const role = useProjectRole(projectId);
  const admin = role.atLeast("admin");
  const [editing, setEditing] = useState<TestAccount | null>(null);
  const [secret, setSecret] = useState<{ account: TestAccount; field: "password" | "totp" } | null>(null);
  const [disabling, setDisabling] = useState<TestAccount | null>(null);
  const [revokingTotp, setRevokingTotp] = useState<TestAccount | null>(null);
  const items = accounts.data?.items ?? [];
  return (
    <section className="card" aria-labelledby="test-accounts-title" data-testid="test-accounts">
      <div className="card-header">
        <UserIcon size={18} />
        <h2 id="test-accounts-title" className="card-title" style={{ flex: 1 }}>
          {t("testAccounts.title")}
        </h2>
      </div>
      <div className="card-body stack">
        <p className="field-hint">{t("testAccounts.intro")}</p>
        {accounts.loading ? (
          <LoadingBlock label={t("common.loading")} />
        ) : accounts.error ? (
          <ErrorBanner error={accounts.error} onRetry={accounts.reload} />
        ) : items.length === 0 ? (
          <p className="subtle">{t("testAccounts.empty")}</p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <caption className="sr-only">{t("testAccounts.title")}</caption>
              <thead>
                <tr>
                  <th scope="col">{t("testAccounts.label")}</th>
                  <th scope="col">{t("testAccounts.username")}</th>
                  <th scope="col">{t("preview.environment")}</th>
                  <th scope="col">{t("testAccounts.hosts")}</th>
                  <th scope="col">{t("testAccounts.credentials")}</th>
                  <th scope="col">{t("inbox.col.state")}</th>
                  <th scope="col">
                    <span className="sr-only">{t("common.actions")}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((a) => (
                  <tr key={a.id} data-testid="test-account-row" data-state={a.state}>
                    <td>
                      {a.label}
                      <span className="subtle" style={{ display: "block" }}>
                        v{a.version}
                      </span>
                    </td>
                    <td className="mono">{a.username}</td>
                    <td>{envName(environments, a.environment_id) ?? <ShortId id={a.environment_id} />}</td>
                    <td className="mono">{a.allowed_hosts.join(", ")}</td>
                    <td>
                      <div className="stack" style={{ gap: 4 }}>
                        <SecretPill state={a.secrets.password.state} label={t("testAccounts.password")} />
                        <SecretPill state={a.secrets.totp.state} label={t("testAccounts.totp")} />
                      </div>
                    </td>
                    <td>
                      <StatePill tone={a.state === "active" ? "success" : "neutral"} label={t(a.state === "active" ? "state.active" : "state.disabled")} />
                    </td>
                    <td>
                      <div className="row row--wrap">
                        {role.atLeast("maintainer") ? (
                          <Button size="sm" onClick={() => setEditing(a)}>
                            {t("common.edit")}
                          </Button>
                        ) : null}
                        {admin ? (
                          <>
                            <Button size="sm" onClick={() => setSecret({ account: a, field: "password" })}>
                              {t("testAccounts.rotatePassword")}
                            </Button>
                            <Button size="sm" onClick={() => setSecret({ account: a, field: "totp" })}>
                              {a.secrets.totp.state === "active" ? t("testAccounts.rotateTotp") : t("testAccounts.setTotp")}
                            </Button>
                            {a.secrets.totp.state === "active" ? (
                              <Button size="sm" variant="ghost" onClick={() => setRevokingTotp(a)}>
                                {t("testAccounts.revokeTotp")}
                              </Button>
                            ) : null}
                            {a.state === "active" ? (
                              <Button size="sm" variant="danger" onClick={() => setDisabling(a)}>
                                {t("testAccounts.disable")}
                              </Button>
                            ) : null}
                          </>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {admin ? <TestAccountForm projectId={projectId} environments={environments} onCreated={accounts.reload} /> : null}
        <SessionsList projectId={projectId} accounts={items} />
      </div>
      <EditTestAccountDialog account={editing} onClose={() => setEditing(null)} onSaved={accounts.reload} />
      <TestAccountSecretDialog target={secret} onClose={() => setSecret(null)} onSaved={accounts.reload} />
      <ConfirmDialog
        open={disabling !== null}
        title={t("testAccounts.disableTitle", { label: disabling?.label ?? "" })}
        body={<p>{t("testAccounts.disableBody")}</p>}
        confirmLabel={t("testAccounts.disable")}
        danger
        onClose={() => setDisabling(null)}
        onConfirm={async () => {
          if (disabling) await disableTestAccount(disabling.id);
          accounts.reload();
        }}
      />
      <ConfirmDialog
        open={revokingTotp !== null}
        title={t("testAccounts.revokeTotp")}
        body={<p>{t("testAccounts.revokeTotpBody")}</p>}
        confirmLabel={t("testAccounts.revokeTotp")}
        danger
        onClose={() => setRevokingTotp(null)}
        onConfirm={async () => {
          if (revokingTotp) await revokeTestAccountTotp(revokingTotp.id);
          accounts.reload();
        }}
      />
    </section>
  );
}

function TestAccountForm({ projectId, environments, onCreated }: { projectId: string; environments: ProjectEnvironment[]; onCreated: () => void }) {
  const { t } = useI18n();
  const { toast } = useToast();
  const [env, setEnv] = useState("");
  const [label, setLabel] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [hosts, setHosts] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [local, setLocal] = useState<Record<string, string>>({});
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!env) errs["environment_id"] = t("testAccounts.envRequired");
    if (!label.trim()) errs["label"] = t("testAccounts.required");
    if (!username.trim()) errs["username"] = t("testAccounts.required");
    if (!password) errs["password"] = t("testAccounts.required");
    if (splitList(hosts).length === 0) errs["allowed_hosts"] = t("testAccounts.hostsRequired");
    setLocal(errs);
    if (Object.keys(errs).length > 0) return;
    setBusy(true);
    setError(null);
    try {
      await createTestAccount(projectId, { environment_id: env, label: label.trim(), username: username.trim(), password, ...(totp.trim() ? { totp_secret: totp.trim() } : {}), allowed_hosts: splitList(hosts) });
      setPassword("");
      setTotp("");
      setLabel("");
      setUsername("");
      setHosts("");
      toast(t("testAccounts.created"), "success");
      onCreated();
    } catch (err) {
      setError(err);
      setPassword("");
      setTotp("");
    } finally {
      setBusy(false);
    }
  };
  const fe = { ...fieldErrors(error), ...local };
  return (
    <details className="disclosure">
      <summary>{t("testAccounts.add")}</summary>
      <form className="stack" onSubmit={submit} noValidate data-testid="test-account-form">
        <ActionError error={error} />
        <FormSection title={t("testAccounts.identity")}>
          <SelectField label={t("preview.environment")} value={env} onChange={(e) => setEnv(e.target.value)} error={fe["environment_id"] ?? null} hint={environments.length === 0 ? t("testAccounts.noEnvironments") : undefined}>
            <option value="">{t("testAccounts.chooseEnv")}</option>
            {environments.map((x) => (
              <option key={x.id} value={x.id}>
                {x.name} ({x.kind})
              </option>
            ))}
          </SelectField>
          <TextField label={t("testAccounts.label")} value={label} onChange={(e) => setLabel(e.target.value)} error={fe["label"] ?? null} maxLength={200} />
          <TextField label={t("testAccounts.username")} value={username} onChange={(e) => setUsername(e.target.value)} error={fe["username"] ?? null} autoComplete="off" spellCheck={false} />
          <TextField label={t("testAccounts.hosts")} hint={t("testAccounts.hostsHint")} value={hosts} onChange={(e) => setHosts(e.target.value)} error={fe["allowed_hosts"] ?? null} spellCheck={false} placeholder="app.example.test, *.example.test" />
        </FormSection>
        <FormSection title={t("testAccounts.credentials")} description={t("testAccounts.writeOnly")}>
          <SecretField label={t("testAccounts.password")} name="test_account_password" value={password} onChange={(e) => setPassword(e.target.value)} storedState={t("settings.secret.notSet")} error={fe["password"] ?? null} />
          <SecretField label={t("testAccounts.totp")} optionalLabel={t("common.optional")} name="test_account_totp" value={totp} onChange={(e) => setTotp(e.target.value)} hint={t("testAccounts.totpHint")} error={fe["totp_secret"] ?? null} />
        </FormSection>
        <div className="row">
          <Button type="submit" variant="primary" loading={busy}>
            {t("testAccounts.create")}
          </Button>
        </div>
      </form>
    </details>
  );
}

function EditTestAccountDialog({ account, onClose, onSaved }: { account: TestAccount | null; onClose: () => void; onSaved: () => void }) {
  const { t } = useI18n();
  const [label, setLabel] = useState("");
  const [username, setUsername] = useState("");
  const [hosts, setHosts] = useState("");
  const [state, setState] = useState<"active" | "disabled">("active");
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  if (account && loadedFor !== `${account.id}:${account.version}`) {
    setLoadedFor(`${account.id}:${account.version}`);
    setLabel(account.label);
    setUsername(account.username);
    setHosts(account.allowed_hosts.join(", "));
    setState(account.state);
    setError(null);
  }
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!account) return;
    setBusy(true);
    setError(null);
    try {
      const nextHosts = splitList(hosts);
      await updateTestAccount(account.id, {
        expected_version: account.version,
        ...(label.trim() !== account.label ? { label: label.trim() } : {}),
        ...(username.trim() !== account.username ? { username: username.trim() } : {}),
        ...(nextHosts.join(",") !== account.allowed_hosts.join(",") ? { allowed_hosts: nextHosts } : {}),
        ...(state !== account.state ? { state } : {}),
      });
      onClose();
      onSaved();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={account !== null} onClose={onClose} title={t("testAccounts.editTitle", { label: account?.label ?? "" })} busy={busy}>
      <form className="stack" onSubmit={submit} noValidate data-testid="test-account-edit">
        <ActionError error={error} onReload={isApiError(error) && error.code === "version_conflict" ? () => { onClose(); onSaved(); } : undefined} />
        <TextField label={t("testAccounts.label")} value={label} onChange={(e) => setLabel(e.target.value)} />
        <TextField label={t("testAccounts.username")} value={username} onChange={(e) => setUsername(e.target.value)} hint={t("testAccounts.usernameChangeHint")} autoComplete="off" />
        <TextField label={t("testAccounts.hosts")} value={hosts} onChange={(e) => setHosts(e.target.value)} spellCheck={false} />
        <SelectField label={t("inbox.col.state")} value={state} onChange={(e) => setState(e.target.value as "active" | "disabled")} hint={t("testAccounts.disableHint")}>
          <option value="active">{t("state.active")}</option>
          <option value="disabled">{t("state.disabled")}</option>
        </SelectField>
        <div className="row row--wrap dialog-actions">
          <Button onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" variant="primary" loading={busy}>
            {t("common.save")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function TestAccountSecretDialog({ target, onClose, onSaved }: { target: { account: TestAccount; field: "password" | "totp" } | null; onClose: () => void; onSaved: () => void }) {
  const { t } = useI18n();
  const { toast } = useToast();
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [openFor, setOpenFor] = useState<string | null>(null);
  const key = target ? `${target.account.id}:${target.field}` : null;
  if (key !== openFor) {
    setOpenFor(key);
    setValue("");
    setError(null);
  }
  const label = target?.field === "totp" ? t("testAccounts.totp") : t("testAccounts.password");
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!target || !value) return;
    setBusy(true);
    setError(null);
    try {
      await putTestAccountSecret(target.account.id, target.field, value);
      setValue("");
      toast(t("testAccounts.secretStored"), "success");
      onClose();
      onSaved();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={target !== null} onClose={onClose} title={t("testAccounts.secretTitle", { label })} busy={busy}>
      <form className="stack" onSubmit={submit} noValidate>
        <p className="field-hint">{t("testAccounts.sessionsRevokedHint")}</p>
        <SecretField label={label} value={value} onChange={(e) => setValue(e.target.value)} hint={target?.field === "totp" ? t("testAccounts.totpHint") : t("settings.secret.writeOnly")} autoFocus />
        <ActionError error={error} />
        <div className="row row--wrap dialog-actions">
          <Button onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" variant="primary" loading={busy} disabled={!value}>
            {t("common.save")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function SessionsList({ projectId, accounts }: { projectId: string; accounts: TestAccount[] }) {
  const { t, formatDateTime } = useI18n();
  const role = useProjectRole(projectId);
  const sessions = useAsync((s) => listBrowserSessions(projectId, s), [projectId]);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const items = sessions.data?.items ?? [];
  const revoke = async (id: string) => {
    setBusy(id);
    setError(null);
    try {
      await revokeBrowserSession(id);
      sessions.reload();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="stack" style={{ gap: 6 }} data-testid="browser-sessions">
      <h3 className="subheading">{t("sessions.title")}</h3>
      {sessions.loading ? (
        <LoadingBlock label={t("common.loading")} lines={2} />
      ) : sessions.error ? (
        <ErrorBanner error={sessions.error} onRetry={sessions.reload} />
      ) : items.length === 0 ? (
        <p className="subtle">{t("sessions.empty")}</p>
      ) : (
        <ul className="plain-list">
          {items.map((s) => (
            <li key={s.id} className="row row--wrap" data-testid="session-row" data-state={s.state}>
              <span>{accounts.find((a) => a.id === s.test_account_id)?.label ?? <ShortId id={s.test_account_id} />}</span>
              <StatePill tone={s.state === "valid" ? "success" : s.state === "expired" ? "warning" : "neutral"} label={t(`sessionState.${s.state}`)} />
              <span className="subtle">{t("sessions.storedIn", { state: t(`secret.${s.storage_state.state}`) })}</span>
              <span className="subtle">{t("sessions.obtained", { when: formatDateTime(s.obtained_at) })}</span>
              {s.expires_at_note ? <span className="field-hint">{s.expires_at_note}</span> : null}
              {role.atLeast("operator") && s.state === "valid" ? (
                <Button size="sm" variant="ghost" onClick={() => void revoke(s.id)} loading={busy === s.id}>
                  {t("sessions.revoke")}
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      <ActionError error={error} onDismiss={() => setError(null)} />
    </div>
  );
}

// ---------------- Scenarios & runs ----------------

const SCENARIO_EXAMPLE = JSON.stringify(
  {
    schema_version: "maestros.browser.scenario.v1",
    id: "home-smoke",
    name: "Home page loads",
    environment: { base_url: "http://127.0.0.1:4173", mode: "local_fixture" },
    sensitive_selectors: [],
    steps: [
      { kind: "goto", url: "http://127.0.0.1:4173/" },
      { kind: "expect_text", text: "Welcome", match: "contains" },
    ],
  },
  null,
  2,
);

const RUN_VIEW: Record<BrowserRun["status"], { tone: "success" | "danger" | "warning" | "info" | "neutral" | "running"; label: MessageKey }> = {
  queued: { tone: "info", label: "runStatus.queued" },
  running: { tone: "running", label: "runStatus.running" },
  passed: { tone: "success", label: "runStatus.passed" },
  failed: { tone: "danger", label: "runStatus.failed" },
  aborted: { tone: "neutral", label: "runStatus.aborted" },
  paused: { tone: "warning", label: "runStatus.paused" },
  rejected: { tone: "danger", label: "runStatus.rejected" },
};

function ScenariosSection({ projectId, environments, accounts }: { projectId: string; environments: ProjectEnvironment[]; accounts: TestAccount[] }) {
  const { t, formatDateTime } = useI18n();
  const role = useProjectRole(projectId);
  const scenarios = useAsync((s) => listScenarios(projectId, {}, s), [projectId]);
  const runs = useAsync((s) => listBrowserRuns(projectId, s), [projectId]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [runResult, setRunResult] = useState<{ scenario: string; error?: unknown; run?: BrowserRun } | null>(null);
  const items = scenarios.data?.items ?? [];

  const changeState = async (s: BrowserScenario, state: "active" | "retired") => {
    setBusy(`${s.id}:${state}`);
    setError(null);
    try {
      await setScenarioState(s.id, state);
      scenarios.reload();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
    }
  };
  const run = async (s: BrowserScenario) => {
    setBusy(`${s.id}:run`);
    setRunResult(null);
    try {
      const r = await startBrowserRun(projectId, { scenario_id: s.id, task_id: null, attempt_id: null });
      setRunResult({ scenario: s.scenario_key, run: r });
      runs.reload();
    } catch (e) {
      setRunResult({ scenario: s.scenario_key, error: e });
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <section className="card" aria-labelledby="scenarios-title" data-testid="scenarios">
        <div className="card-header">
          <KeyIcon size={18} />
          <h2 id="scenarios-title" className="card-title" style={{ flex: 1 }}>
            {t("scenarios.title")}
          </h2>
        </div>
        <div className="card-body stack">
          {scenarios.loading ? (
            <LoadingBlock label={t("common.loading")} />
          ) : scenarios.error ? (
            <ErrorBanner error={scenarios.error} onRetry={scenarios.reload} />
          ) : items.length === 0 ? (
            <p className="subtle">{t("scenarios.empty")}</p>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <caption className="sr-only">{t("scenarios.title")}</caption>
                <thead>
                  <tr>
                    <th scope="col">{t("scenarios.key")}</th>
                    <th scope="col">{t("inbox.col.state")}</th>
                    <th scope="col">{t("preview.environment")}</th>
                    <th scope="col">{t("scenarios.account")}</th>
                    <th scope="col">{t("scenarios.hash")}</th>
                    <th scope="col">
                      <span className="sr-only">{t("common.actions")}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((s) => (
                    <tr key={s.id} data-testid="scenario-row" data-state={s.state}>
                      <td>
                        <span className="mono">{s.scenario_key}</span> <span className="subtle">v{s.version}</span>
                        <span className="subtle" style={{ display: "block" }}>
                          {formatDateTime(s.created_at)}
                        </span>
                      </td>
                      <td>
                        <StatePill tone={s.state === "active" ? "success" : s.state === "draft" ? "warning" : "neutral"} label={t(`scenarioState.${s.state}`)} />
                      </td>
                      <td>{envName(environments, s.environment_id) ?? <ShortId id={s.environment_id} />}</td>
                      <td>{s.test_account_id ? (accounts.find((a) => a.id === s.test_account_id)?.label ?? <ShortId id={s.test_account_id} />) : t("scenarios.noAccount")}</td>
                      <td>
                        <Hash value={s.body_hash} />
                      </td>
                      <td>
                        <div className="row row--wrap">
                          {role.atLeast("operator") && s.state !== "retired" ? (
                            <Button size="sm" variant="primary" onClick={() => void run(s)} loading={busy === `${s.id}:run`}>
                              {t("scenarios.run")}
                            </Button>
                          ) : null}
                          {role.atLeast("maintainer") && s.state === "draft" ? (
                            <Button size="sm" onClick={() => void changeState(s, "active")} loading={busy === `${s.id}:active`}>
                              {t("scenarios.activate")}
                            </Button>
                          ) : null}
                          {role.atLeast("maintainer") && s.state !== "retired" ? (
                            <Button size="sm" variant="ghost" onClick={() => void changeState(s, "retired")} loading={busy === `${s.id}:retired`}>
                              {t("scenarios.retire")}
                            </Button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <ActionError error={error} onDismiss={() => setError(null)} />
          {runResult ? (
            <div data-testid="run-result">
              <p className="subheading">{t("scenarios.runResult", { key: runResult.scenario })}</p>
              {runResult.error ? <ActionError error={runResult.error} /> : <p className="subtle">{t("scenarios.runAccepted")}</p>}
            </div>
          ) : null}
          {role.atLeast("maintainer") ? <ScenarioForm projectId={projectId} environments={environments} accounts={accounts} onCreated={scenarios.reload} /> : null}
        </div>
      </section>
      <RunsSection runs={runs} scenarios={items} />
    </>
  );
}

function ScenarioForm({ projectId, environments, accounts, onCreated }: { projectId: string; environments: ProjectEnvironment[]; accounts: TestAccount[]; onCreated: () => void }) {
  const { t } = useI18n();
  const { toast } = useToast();
  const [env, setEnv] = useState("");
  const [account, setAccount] = useState("");
  const [body, setBody] = useState(SCENARIO_EXAMPLE);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [local, setLocal] = useState<Record<string, string>>({});
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const errs: Record<string, string> = {};
    let parsed: Record<string, unknown> | null = null;
    if (!env) errs["environment_id"] = t("testAccounts.envRequired");
    try {
      const v: unknown = JSON.parse(body);
      if (v && typeof v === "object" && !Array.isArray(v)) parsed = v as Record<string, unknown>;
      else errs["body"] = t("scenarios.bodyObject");
    } catch {
      errs["body"] = t("scenarios.bodyJson");
    }
    setLocal(errs);
    if (Object.keys(errs).length > 0 || !parsed) return;
    setBusy(true);
    setError(null);
    try {
      await createScenario(projectId, { environment_id: env, test_account_id: account || null, state: "draft", body: parsed });
      toast(t("scenarios.created"), "success");
      onCreated();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  const fe = { ...fieldErrors(error), ...local };
  return (
    <details className="disclosure">
      <summary>{t("scenarios.add")}</summary>
      <form className="stack" onSubmit={submit} noValidate data-testid="scenario-form">
        <ActionError error={error} />
        <div className="w3-toolbar">
          <SelectField label={t("preview.environment")} value={env} onChange={(e) => setEnv(e.target.value)} error={fe["environment_id"] ?? null}>
            <option value="">{t("testAccounts.chooseEnv")}</option>
            {environments.map((x) => (
              <option key={x.id} value={x.id}>
                {x.name} ({x.kind})
              </option>
            ))}
          </SelectField>
          <SelectField label={t("scenarios.account")} value={account} onChange={(e) => setAccount(e.target.value)} optionalLabel={t("common.optional")}>
            <option value="">{t("scenarios.noAccount")}</option>
            {accounts
              .filter((a) => a.state === "active" && (!env || a.environment_id === env))
              .map((a) => (
                <option key={a.id} value={a.id}>
                  {a.label}
                </option>
              ))}
          </SelectField>
        </div>
        <TextAreaField label={t("scenarios.body")} hint={t("scenarios.bodyHint")} value={body} onChange={(e) => setBody(e.target.value)} error={fe["body"] ?? null} rows={10} spellCheck={false} className="mono" />
        <div className="row">
          <Button type="submit" variant="primary" loading={busy}>
            {t("scenarios.create")}
          </Button>
        </div>
      </form>
    </details>
  );
}

function RunsSection({ runs, scenarios }: { runs: AsyncState<{ items: BrowserRun[]; egress_control_label: string }>; scenarios: BrowserScenario[] }) {
  const { t, formatDateTime } = useI18n();
  const items = runs.data?.items ?? [];
  return (
    <section className="card" aria-labelledby="browser-runs-title" data-testid="browser-runs">
      <div className="card-header">
        <PlayCircleIcon size={18} />
        <h2 id="browser-runs-title" className="card-title" style={{ flex: 1 }}>
          {t("browserRuns.title")}
        </h2>
        <Button size="sm" variant="ghost" icon={<RefreshIcon size={14} />} onClick={runs.reload}>
          {t("common.refresh")}
        </Button>
      </div>
      <div className="card-body stack">
        <p className="field-hint">{t("browserRuns.blockedNote")}</p>
        {runs.loading ? (
          <LoadingBlock label={t("common.loading")} />
        ) : runs.error ? (
          <ErrorBanner error={runs.error} onRetry={runs.reload} />
        ) : items.length === 0 ? (
          <EmptyState icon={<PlayCircleIcon />} title={t("browserRuns.emptyTitle")} headingLevel={3}>
            <p>{t("browserRuns.emptyBody")}</p>
          </EmptyState>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <caption className="sr-only">{t("browserRuns.title")}</caption>
              <thead>
                <tr>
                  <th scope="col">{t("browserRuns.scenario")}</th>
                  <th scope="col">{t("browserRuns.status")}</th>
                  <th scope="col">{t("browserRuns.mode")}</th>
                  <th scope="col">{t("browserRuns.blocked")}</th>
                  <th scope="col">{t("browserRuns.created")}</th>
                  <th scope="col">{t("browserRuns.trace")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => {
                  const view = RUN_VIEW[r.status];
                  const scenario = scenarios.find((s) => s.id === r.scenario_id);
                  return (
                    <tr key={r.id} data-testid="browser-run-row" data-status={r.status}>
                      <td>{scenario ? <span className="mono">{scenario.scenario_key} v{scenario.version}</span> : <ShortId id={r.scenario_id} />}</td>
                      <td>
                        <StatePill tone={view.tone} label={t(view.label)} />
                        {r.failure_class ? <span className="badge mono">{r.failure_class}</span> : null}
                      </td>
                      <td>
                        <span className="badge">{t(`runMode.${r.mode}`)}</span>
                        <span className="field-hint" style={{ display: "block" }}>
                          {r.evidence_label}
                        </span>
                      </td>
                      <td>
                        {r.blocked_requests.length === 0 ? (
                          <span className="subtle">0</span>
                        ) : (
                          <details>
                            <summary>{t("browserRuns.blockedCount", { n: r.blocked_requests.length })}</summary>
                            <ul className="notes">
                              {r.blocked_requests.map((b, i) => (
                                <li key={i} className="mono">
                                  {b.host ?? "?"}
                                  {b.port ? `:${b.port}` : ""} — {b.reason}
                                </li>
                              ))}
                            </ul>
                          </details>
                        )}
                      </td>
                      <td>{formatDateTime(r.created_at)}</td>
                      <td>
                        {r.trace_download_url ? (
                          <a href={r.trace_download_url} download className="link-subtle">
                            {t("browserRuns.downloadTrace")}
                          </a>
                        ) : (
                          <span className="subtle">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <p>
          <EgressLabel label={runs.data?.egress_control_label ?? URL_FILTER_LABEL} />
        </p>
      </div>
    </section>
  );
}
